import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";

/**
 * Beeta — Live Network Map
 * Real Beeta hubs plotted as % positions within the India silhouette
 * (SVG is centered inside a 5:6 aspect container, so India occupies
 *  x ≈ 20–88 %, y ≈ 18–82 % of the container).
 * Coordinates chosen to (a) sit inside India's outline, (b) avoid label collisions.
 */
const hubs = [
    // Coordinates derived from real lat/lon calibrated against the india-map.svg silhouette:
    // x = 2.9 + (lon - 68.1) * 3.18   |   y = (35.26 - lat) * 3.675
    // Tight clusters (NCR, South) are fanned slightly (±1-2%) for label legibility.

    // NORTH — Punjab / Chandigarh
    { id: "ldh", name: "Ludhiana",      label: "Branch",         x: 27.6, y: 15.6, size: "sm", labelPos: "top" },
    { id: "chd", name: "Chandigarh",    label: "Branch",         x: 30.9, y: 16.2, size: "sm", labelPos: "right" },
    { id: "moh", name: "Mohali",        label: "Branch",         x: 29.7, y: 17.8, size: "sm", labelPos: "bottom" },

    // NORTH — NCR belt (fanned out for clear labels)
    { id: "del", name: "Delhi",         label: "Branch",         x: 32.4, y: 23.2, size: "sm", labelPos: "top" },
    { id: "noi", name: "Gr. Noida",     label: "Warehouse",      x: 34, y: 24.6, size: "md", labelPos: "right" },
    { id: "ggn", name: "Gurgaon",       label: "HQ · Sector 74A", x: 31.2, y: 25.2, size: "lg", labelPos: "left" },
    { id: "fbd", name: "Faridabad",     label: "Branch",         x: 33.4, y: 26.9, size: "sm", labelPos: "right" },
    { id: "man", name: "Manesar",       label: "Warehouse",      x: 30, y: 27.8, size: "sm", labelPos: "left" },
    { id: "drh", name: "Dharuhera",     label: "Branch",         x: 31.8, y: 28.9, size: "sm", labelPos: "right" },
    { id: "bwl", name: "Bawal",         label: "Branch",         x: 29.2, y: 29.6, size: "md", labelPos: "left" },

    // WEST — Maharashtra
    { id: "nsk", name: "Nashik",        label: "Branch",         x: 21, y: 55.8, size: "sm", labelPos: "top" },
    { id: "thn", name: "Thane",         label: "Branch",         x: 17.2, y: 58.9, size: "sm", labelPos: "left" },
    { id: "mum", name: "Mumbai",        label: "Branch",         x: 17.9, y: 59.5, size: "md", labelPos: "left" },
    { id: "ckn", name: "Chakan",        label: "Warehouse",      x: 21.6, y: 59.9, size: "sm", labelPos: "right" },
    { id: "pun", name: "Pune",          label: "Branch",         x: 20.8, y: 62, size: "lg", labelPos: "right" },

    // SOUTH — Karnataka & Tamil Nadu (fanned out for clear labels)
    { id: "nar", name: "Narasapura",    label: "Warehouse",      x: 34.6, y: 80.4, size: "sm", labelPos: "top" },
    { id: "blr", name: "Bengaluru",     label: "Branch",         x: 32.6, y: 82.2, size: "lg", labelPos: "left" },
    { id: "sri", name: "Sricity",       label: "Branch",         x: 40.8, y: 78.8, size: "sm", labelPos: "right" },
    { id: "maa", name: "Chennai",       label: "Branch",         x: 41.6, y: 81.2, size: "lg", labelPos: "right" },
    { id: "spr", name: "Sriperumbudur", label: "Warehouse",      x: 40.6, y: 83.6, size: "sm", labelPos: "left" },
    { id: "hos", name: "Hosur",         label: "Branch",         x: 34.2, y: 84.6, size: "sm", labelPos: "bottom" },

    // NORTH EAST — Assam + 6 states
    { id: "ghy", name: "Guwahati",      label: "Warehouse + Branch", x: 78.1, y: 33.5, size: "md", labelPos: "left" },
    { id: "shl", name: "Shillong",      label: "Branch",         x: 77.5, y: 35.6, size: "sm", labelPos: "left" },
    { id: "itn", name: "Itanagar",      label: "Branch",         x: 83.0, y: 30.0, size: "sm", labelPos: "top" },
    { id: "koh", name: "Kohima",        label: "Branch",         x: 84.6, y: 35.3, size: "sm", labelPos: "right" },
    { id: "imp", name: "Imphal",        label: "Branch",         x: 84.0, y: 38.4, size: "sm", labelPos: "right" },
    { id: "aiz", name: "Aizawl",        label: "Branch",         x: 80.2, y: 42.4, size: "sm", labelPos: "right" },
    { id: "agr", name: "Agartala",      label: "Branch",         x: 75.6, y: 42.0, size: "sm", labelPos: "left" },
];

// Primary corridors only — matches the "Primary corridors" list
const lanes = [
    { from: "ggn", to: "man", intensity: "high" },
    { from: "man", to: "bwl", intensity: "high" },
    { from: "ggn", to: "noi", intensity: "high" },
    { from: "ggn", to: "pun", intensity: "high" },
    { from: "ggn", to: "blr", intensity: "high" },
    { from: "ggn", to: "maa", intensity: "high" },
    { from: "ggn", to: "mum", intensity: "med" },
    { from: "ggn", to: "ghy", intensity: "med" },
];

const getHub = (id) => hubs.find((h) => h.id === id);

export default function NetworkMap() {
    const [hoveredHubId, setHoveredHubId] = useState(null);
    return (
        <section className="relative py-24 sm:py-32 bg-slate-900 text-white overflow-hidden">
            {/* dotted backdrop */}
            <div className="absolute inset-0 bg-dot-slate opacity-[0.07]" />
            <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    {/* Left: Copy + KPIs */}
                    <div className="lg:col-span-3">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow mb-4">
                            Live Network
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-tight">
                            25+ cities.
                            <br />
                            <span className="text-brand-yellow">One network.</span>
                        </h2>
                        <p className="mt-6 text-slate-400 leading-relaxed">
                            Seven-plus warehouses and twenty-plus branch locations built
                            around India&apos;s industrial belts — Gurgaon-Manesar-Bawal
                            in the north, Mumbai-Chakan-Pune in the west, Sriperumbudur
                            and Sricity in the south, and full pin-code coverage across
                            the North East out of Guwahati.
                        </p>
                        <p className="mt-4 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-yellow mr-2 align-middle" />
                            Hover any hub to light up its lane
                        </p>

                        <div className="mt-10 grid grid-cols-3 gap-3">
                            {[
                                { v: "25+", l: "Cities" },
                                { v: "7+", l: "Warehouses" },
                                { v: "27+", l: "Branches" },
                            ].map((k) => (
                                <div
                                    key={k.l}
                                    className="p-4 rounded-xl border border-slate-800 bg-slate-800/30"
                                >
                                    <div className="font-display text-2xl font-black text-white tracking-tight">
                                        {k.v}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mt-1 font-semibold">
                                        {k.l}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: Map */}
                    <div className="lg:col-span-9">
                        <div className="relative mx-auto w-full aspect-square" data-testid="network-map-container">
                            {/* India silhouette */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <img
                                    src="/assets/india-map.svg"
                                    alt="India"
                                    className="w-full h-full object-contain select-none pointer-events-none"
                                    style={{
                                        filter: "brightness(0) invert(1)",
                                        opacity: 0.08,
                                    }}
                                    draggable={false}
                                />
                            </div>

                            {/* Glow under map */}
                            <div className="absolute inset-10 rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />

                            {/* Lanes SVG */}
                            <svg
                                viewBox="0 0 100 100"
                                preserveAspectRatio="none"
                                className="absolute inset-0 w-full h-full"
                                aria-hidden="true"
                                data-testid="network-map-svg"
                            >
                                <defs>
                                    <linearGradient id="lane-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#0033CC" stopOpacity="0" />
                                        <stop offset="50%" stopColor="#FFC300" stopOpacity="0.95" />
                                        <stop offset="100%" stopColor="#0033CC" stopOpacity="0" />
                                    </linearGradient>
                                    <radialGradient id="pulse-radial" cx="50%" cy="50%" r="50%">
                                        <stop offset="0%" stopColor="#FFDA47" stopOpacity="1" />
                                        <stop offset="60%" stopColor="#FFC300" stopOpacity="0.75" />
                                        <stop offset="100%" stopColor="#FFC300" stopOpacity="0" />
                                    </radialGradient>
                                </defs>

                                {/* NE / Guwahati hover ring — highlights the North East cluster */}
                                <circle
                                    cx="80"
                                    cy="37"
                                    r="9"
                                    fill="url(#pulse-radial)"
                                    style={{
                                        opacity: hoveredHubId === "ghy" ? 0.35 : 0,
                                        transition: "opacity 300ms ease",
                                    }}
                                    data-testid="ne-highlight-ring"
                                />

                                {lanes.map((lane, i) => {
                                    const a = getHub(lane.from);
                                    const b = getHub(lane.to);
                                    const isHovered =
                                        hoveredHubId &&
                                        (lane.from === hoveredHubId || lane.to === hoveredHubId);
                                    const baseOpacity =
                                        lane.intensity === "high" ? 0.55 : lane.intensity === "med" ? 0.35 : 0.2;
                                    const opacity = isHovered ? 0.95 : baseOpacity;
                                    const pulseDur = lane.intensity === "high" ? 3.5 : 4.5;
                                    return (
                                        <g key={i} data-testid={`lane-${lane.from}-${lane.to}`}>
                                            {/* static baseline */}
                                            <line
                                                x1={a.x}
                                                y1={a.y}
                                                x2={b.x}
                                                y2={b.y}
                                                stroke={isHovered ? "#FFC300" : "#3B82F6"}
                                                strokeOpacity={opacity}
                                                strokeWidth={isHovered ? "0.6" : "0.3"}
                                                strokeDasharray={isHovered ? "0" : "0.6 0.8"}
                                                vectorEffect="non-scaling-stroke"
                                                style={{ transition: "stroke 250ms ease, stroke-opacity 250ms ease, stroke-width 250ms ease" }}
                                            />
                                            {/* animated pulse beam */}
                                            <line
                                                x1={a.x}
                                                y1={a.y}
                                                x2={b.x}
                                                y2={b.y}
                                                stroke="url(#lane-gradient)"
                                                strokeWidth="0.7"
                                                strokeLinecap="round"
                                                strokeDasharray="3 40"
                                                vectorEffect="non-scaling-stroke"
                                                strokeOpacity={isHovered ? 0.9 : 0.5}
                                                style={{
                                                    animation: "lane-beam 3.2s linear infinite",
                                                    animationDelay: `-${i * 0.55}s`,
                                                    transition: "stroke-opacity 250ms ease",
                                                }}
                                            />
                                            {/* travelling pulse dot — high & med lanes only */}
                                            {lane.intensity !== "low" && (
                                                <circle
                                                    cx={a.x}
                                                    cy={a.y}
                                                    r={lane.intensity === "high" ? 0.9 : 0.65}
                                                    fill="url(#pulse-radial)"
                                                    opacity="0"
                                                    data-testid={`lane-pulse-${lane.from}-${lane.to}`}
                                                >
                                                    <animate
                                                        attributeName="cx"
                                                        from={a.x}
                                                        to={b.x}
                                                        dur={`${pulseDur}s`}
                                                        begin={`-${i * 0.7}s`}
                                                        repeatCount="indefinite"
                                                    />
                                                    <animate
                                                        attributeName="cy"
                                                        from={a.y}
                                                        to={b.y}
                                                        dur={`${pulseDur}s`}
                                                        begin={`-${i * 0.7}s`}
                                                        repeatCount="indefinite"
                                                    />
                                                    <animate
                                                        attributeName="opacity"
                                                        values="0;1;1;0"
                                                        keyTimes="0;0.2;0.8;1"
                                                        dur={`${pulseDur}s`}
                                                        begin={`-${i * 0.7}s`}
                                                        repeatCount="indefinite"
                                                    />
                                                </circle>
                                            )}
                                        </g>
                                    );
                                })}
                            </svg>

                            {/* Hub markers (HTML absolute — for easy labeling) */}
                            {hubs.map((h, i) => {
                                const isLarge = h.size === "lg";
                                const isSmall = h.size === "sm";
                                return (
                                    <motion.div
                                        key={h.id}
                                        initial={{ opacity: 0, scale: 0.3 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: 0.15 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                                        className="absolute -translate-x-1/2 -translate-y-1/2 group"
                                        style={{ left: `${h.x}%`, top: `${h.y}%` }}
                                        data-testid={`hub-${h.id}`}
                                        onMouseEnter={() => setHoveredHubId(h.id)}
                                        onMouseLeave={() => setHoveredHubId((prev) => (prev === h.id ? null : prev))}
                                    >
                                        {/* pulse — only large/medium */}
                                        {!isSmall && (
                                            <span
                                                className={`absolute rounded-full bg-brand-yellow/40 animate-ping ${
                                                    isLarge ? "w-6 h-6" : "w-4 h-4"
                                                }`}
                                                style={{
                                                    left: "50%",
                                                    top: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    animationDuration: "2.4s",
                                                    animationDelay: `${(i % 4) * 0.6}s`,
                                                }}
                                            />
                                        )}
                                        {/* dot */}
                                        <span
                                            className={`relative block rounded-full ring-2 ring-slate-900 ${
                                                isLarge
                                                    ? "w-4 h-4 bg-brand-yellow"
                                                    : isSmall
                                                    ? "w-2 h-2 bg-slate-300"
                                                    : "w-3 h-3 bg-blue-500"
                                            }`}
                                        />
                                        {/* enlarged hover hit-area for small dots */}
                                        <span className="absolute -inset-2.5" />
                                        {/* label — permanent for major hubs & warehouses; hover-reveal for the rest */}
                                        <div
                                            className={`absolute whitespace-nowrap transition-opacity duration-200 ${
                                                isLarge
                                                    ? "opacity-100"
                                                    : !isSmall
                                                    ? `opacity-100 ${h.id === "ghy" ? "" : "max-sm:hidden"}`
                                                    : "opacity-0 group-hover:opacity-100 group-hover:z-20 pointer-events-none"
                                            } ${
                                                h.labelPos === "top"
                                                    ? "left-1/2 bottom-full mb-2 -translate-x-1/2"
                                                    : h.labelPos === "left"
                                                    ? "right-full mr-2 top-1/2 -translate-y-1/2"
                                                    : h.labelPos === "right"
                                                    ? "left-full ml-2 top-1/2 -translate-y-1/2"
                                                    : "left-1/2 top-full mt-2 -translate-x-1/2"
                                            }`}
                                        >
                                            {!isSmall ? (
                                                <div className="px-1.5 py-1 rounded-md bg-white/95 text-slate-900 shadow-lg">
                                                    <div className="font-display text-[10px] font-black tracking-tight leading-none">
                                                        {h.name}
                                                    </div>
                                                    <div className="text-[7px] font-semibold text-slate-500 leading-none mt-0.5 uppercase tracking-wider">
                                                        {h.label}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="px-1.5 py-1 rounded-md bg-slate-900/90 backdrop-blur text-white ring-1 ring-white/15 shadow-lg">
                                                    <div className="text-[9px] font-bold tracking-tight leading-none">
                                                        {h.name}
                                                    </div>
                                                    <div className="text-[7px] font-semibold text-slate-400 leading-none mt-0.5 uppercase tracking-wider">
                                                        {h.label}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}

                            {/* Compass badge */}
                            <div className="hidden sm:flex absolute bottom-4 right-4 items-center gap-2 px-3 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10">
                                <span className="relative flex w-2 h-2">
                                    <span className="absolute inset-0 rounded-full bg-brand-yellow opacity-60 animate-ping" />
                                    <span className="relative w-2 h-2 rounded-full bg-brand-yellow" />
                                </span>
                                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-200">
                                    Live · updated every 15s
                                </span>
                            </div>

                            {/* Scale indicator */}
                            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 sm:bottom-4 sm:left-4 sm:translate-x-0 flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10">
                                <MapPin className="w-3 h-3 text-brand-yellow" />
                                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-200">
                                    India · 25+ cities
                                    <span className="hidden lg:inline text-slate-400"> · hover dots for details</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes lane-beam {
                    0% { stroke-dashoffset: 43; }
                    100% { stroke-dashoffset: 0; }
                }
            `}</style>
        </section>
    );
}
